import type {
    FilteredValue,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';

import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { StockageModel } from 'src/app/models/stock/stokage.model';

export interface StockEntradaViverosmuzaleSyncContext {
    readonly isCreation: boolean;
    readonly model: ReactiveFormValue<StockageModel>;
    readonly rawValues: Record<string, unknown>;
    readonly parcelas: ReadonlyArray<FilteredValue<ParcelasModel>>;
    readonly extractId: (value: unknown) => string;
}

export type StockEntradaPlantasSyncPayloadContext = StockEntradaViverosmuzaleSyncContext;

export interface StockEntradaCultivoParcelaSyncPayloadContext extends StockEntradaViverosmuzaleSyncContext {
    readonly cultivos: ReadonlyArray<FilteredValue<Record<string, unknown>>>;
}

export interface StockEntradaPlantasSyncPayloadResult {
    readonly payload: {
        readonly id: string;
        readonly plantas: string;
    };
    readonly parcela: ParcelasModel;
    readonly plantas: string;
    readonly ubicacionId: string;
}

export interface StockEntradaCultivoParcelaSyncPayloadResult {
    readonly payload: {
        readonly id: string;
        readonly id_cultivo: string;
        readonly id_variedad: string;
    };
    readonly parcela: ParcelasModel;
    readonly idCultivo: string;
    readonly idVariedad: string;
    readonly ubicacionId: string;
}

/**
 * Builds the parcel plant-count update caused by a Viveros Muzale stock entry/exit.
 */
export function buildStockEntradaPlantasSyncPayload(
    context: StockEntradaPlantasSyncPayloadContext,
): StockEntradaPlantasSyncPayloadResult | null {
    if (!context.isCreation) {
        return null;
    }

    const tipo = String(context.model.tipo ?? '');

    if (tipo !== 'E' && tipo !== 'S') {
        return null;
    }

    if (!isStockEntradaPlanta(context.model)) {
        return null;
    }

    const ubicacionId = context.extractId(context.rawValues['ubicacion']);

    if (ubicacionId === '') {
        return null;
    }

    const udsProducidas = parseStockEntradaDecimal(context.model['uds_producidas']);

    if (udsProducidas === 0) {
        return null;
    }

    const parcela = findStockEntradaParcela(context.parcelas, ubicacionId);

    if (!parcela) {
        return null;
    }

    const plantasActuales = parseStockEntradaDecimal(parcela.plantas);
    const nuevasPlantas = tipo === 'E'
        ? plantasActuales + udsProducidas
        : plantasActuales - udsProducidas;
    const plantas = String(nuevasPlantas);

    return {
        payload: {
            id: ubicacionId,
            plantas,
        },
        parcela,
        plantas,
        ubicacionId,
    };
}

/**
 * Builds the parcel crop/variety update caused by a Viveros Muzale planting stock entry.
 */
export function buildStockEntradaCultivoParcelaSyncPayload(
    context: StockEntradaCultivoParcelaSyncPayloadContext,
): StockEntradaCultivoParcelaSyncPayloadResult | null {
    if (!context.isCreation) {
        return null;
    }

    if (String(context.model.tipo ?? '') !== 'P') {
        return null;
    }

    if (!isStockEntradaPlanta(context.model)) {
        return null;
    }

    const ubicacionId = context.extractId(context.rawValues['ubicacion']);

    if (ubicacionId === '') {
        return null;
    }

    const idProducto = String(context.model.ids_productos ?? '').trim();
    const idVariedad = String(context.model.id_variedad ?? '').trim();

    if (idProducto === '') {
        return null;
    }

    const idCultivo = findStockEntradaCultivoCodigo(context.cultivos, idProducto);

    if (idCultivo === '') {
        return null;
    }

    const parcela = findStockEntradaParcela(context.parcelas, ubicacionId);

    if (!parcela) {
        return null;
    }

    return {
        payload: {
            id: ubicacionId,
            id_cultivo: idCultivo,
            id_variedad: idVariedad,
        },
        parcela,
        idCultivo,
        idVariedad,
        ubicacionId,
    };
}

function isStockEntradaPlanta(model: ReactiveFormValue<StockageModel>): boolean {
    return String(model.tipo_dato ?? '') === 'Planta';
}

function parseStockEntradaDecimal(value: unknown): number {
    return Number(String(value ?? '0').replace(',', '.'));
}

function findStockEntradaParcela(
    parcelas: ReadonlyArray<FilteredValue<ParcelasModel>>,
    parcelaId: string,
): ParcelasModel | null {
    const found = parcelas.find(
        (item) => String(item.value?.id ?? '') === parcelaId
    );

    return found?.value ?? null;
}

function findStockEntradaCultivoCodigo(
    cultivos: ReadonlyArray<FilteredValue<Record<string, unknown>>>,
    productoId: string,
): string {
    const found = cultivos.find(
        (item) => String(item.value?.['id'] ?? '') === productoId
    );

    return String(found?.value?.['codigo_cultivo'] ?? '').trim();
}
